import {
    ChangeDetectionStrategy,
    Component,
    computed,
    effect,
    inject,
    signal,
    viewChildren,
} from '@angular/core';
import {AsyncPipe, NgClass} from '@angular/common';
import {ActivatedRoute, Router} from '@angular/router';
import {toObservable, toSignal} from '@angular/core/rxjs-interop';
import {BehaviorSubject, catchError, combineLatest, filter, map, shareReplay, tap} from 'rxjs';
import {Store} from '@ngrx/store';
import {
    switchMapWithLoading,
    TRANSLATE,
    TranslatePipe,
    includesIgnoreCase,
    caseInsensitiveComparator,
    fromControl,
} from '@brunata-angular/util';
import {
    LoadComponent,
    applySortingToHeaders,
    SortableTableHeaderComponent,
    InputTextGroupComponent,
    Sorting,
    PaginationComponent,
    InputDropdownCheckboxesComponent,
    SelectableItem,
} from '@brunata-angular/ui';
import {of} from 'rxjs';
import {ReactiveFormsModule, NonNullableFormBuilder} from '@angular/forms';
import {
    UserAdministrationService,
    UserListItem,
} from '@shared/services/user-administration.service';
import {selectUser} from 'src/app/state/selectors';
import {UserStateSuccess} from 'src/app/state/reducers';

const NOTE_DISPLAY_MAX_LENGTH = 50;

type UserRow = UserListItem & {
    noteFull: string;
    noteDisplay: string;
};

@Component({
    selector: 'app-user-administration',
    standalone: true,
    imports: [
        AsyncPipe,
        NgClass,
        LoadComponent,
        TranslatePipe,
        SortableTableHeaderComponent,
        InputTextGroupComponent,
        ReactiveFormsModule,
        PaginationComponent,
        InputDropdownCheckboxesComponent,
    ],
    templateUrl: './user-administration.component.html',
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class UserAdministrationComponent {
    private readonly headers = viewChildren(SortableTableHeaderComponent);

    private readonly userService = inject(UserAdministrationService);
    private readonly router = inject(Router);
    private readonly route = inject(ActivatedRoute);
    private readonly fb = inject(NonNullableFormBuilder);
    private readonly translate = inject(TRANSLATE);
    private readonly store = inject(Store);

    // Only administrators (the logged-in user) may create users; a 'User'-role sub-user must not
    // see the action (the backend rejects it too).
    private readonly currentUserRole = toSignal(
        this.store.select(selectUser).pipe(
            filter((state): state is UserStateSuccess => state.status === 'success'),
            map(state => state.user.role),
        ),
    );
    protected readonly canCreateUser = computed(() => this.currentUserRole() === 'ADMINISTRATOR');

    readonly page = signal(1);
    readonly pageSize = signal(10);

    private readonly adminRoleLabel = this.translate(
        'properties_roles.administrator',
        'online',
        'userAdministration',
    );
    private readonly userRoleLabel = this.translate(
        'properties_roles.user',
        'online',
        'userAdministration',
    );
    // The shared dropdown renders (and round-trips) SelectableItem.name verbatim, so name
    // holds the translated label; filtering maps that label back to the untranslated role key.
    private readonly roleLabelToRoleKey = new Map<string, string>([
        [this.adminRoleLabel, 'administrator'],
        [this.userRoleLabel, 'user'],
    ]);

    readonly formGroup = this.fb.group({
        name: [''],
        roles: [
            [
                {name: this.adminRoleLabel, selected: false},
                {name: this.userRoleLabel, selected: false},
            ] as SelectableItem[],
        ],
        note: [''],
    });

    private readonly sorting$ = new BehaviorSubject<Sorting>({
        column: 'name',
        direction: 'asc',
    });
    private readonly sorting = toSignal(this.sorting$, {requireSync: true});

    private readonly usersSource$ = of(null).pipe(
        switchMapWithLoading(() => this.userService.getUsers()),
        shareReplay(1),
    );

    readonly users$ = combineLatest([
        this.usersSource$,
        fromControl(this.formGroup).pipe(tap(() => this.page.set(1))),
        this.sorting$,
        toObservable(this.page),
        toObservable(this.pageSize),
    ]).pipe(
        map(([usersResponse, form, sorting, page, pageSize]) => {
            if (usersResponse.loading || usersResponse.value === undefined) {
                return null;
            }
            const users = usersResponse.value ?? [];

            const nameSearch = form.name ?? '';
            const noteSearch = form.note ?? '';
            const selectedRoles =
                form.roles
                    ?.filter(r => r.selected)
                    .map(r => this.roleLabelToRoleKey.get(r.name) ?? '') ?? [];
            const hasRoleFilter = selectedRoles.length > 0;
            const selectedRolesSet = new Set(selectedRoles);

            const filtered = users.filter(
                user =>
                    includesIgnoreCase(user.personName ?? user.userName, nameSearch) &&
                    includesIgnoreCase(user.note ?? '', noteSearch) &&
                    (!hasRoleFilter || selectedRolesSet.has(user.role.toLowerCase())),
            );

            filtered.sort(
                caseInsensitiveComparator(user => {
                    if (sorting.column === 'name') {
                        return user.personName ?? user.userName;
                    }
                    if (sorting.column === 'role') {
                        return user.role;
                    }
                    if (sorting.column === 'note') {
                        return user.note ?? '';
                    }
                    return '';
                }, sorting.direction ?? 'asc'),
            );

            const collectionSize = filtered.length;
            const startIdx = (page - 1) * pageSize;
            const endIdx = page * pageSize;
            const paginated = filtered.slice(startIdx, endIdx).map(
                (user): UserRow => ({
                    ...user,
                    noteFull: user.note ?? '',
                    noteDisplay: this.truncateNote(user.note ?? ''),
                }),
            );

            return {
                users: paginated,
                collectionSize,
            };
        }),
        // The global ErrorInterceptor shows the error toast; degrade to an empty list so
        // the table renders its empty state instead of the loading spinner spinning forever.
        catchError(() => of({users: new Array<UserRow>(), collectionSize: 0})),
    );

    constructor() {
        effect(() => {
            applySortingToHeaders(this.headers(), this.sorting());
        });
    }

    onSort(sorting: Sorting): void {
        this.sorting$.next(sorting);
    }

    setPage(page: number): void {
        this.page.set(page);
    }

    setPageSize(pageSize: number): void {
        this.pageSize.set(pageSize);
    }

    async navigateToUser(user: UserListItem): Promise<void> {
        await this.router.navigate([user.loginNameId.toString()], {relativeTo: this.route});
    }

    async navigateToCreateUser(): Promise<void> {
        await this.router.navigate(['create-user'], {relativeTo: this.route});
    }

    private truncateNote(note: string): string {
        return note.length > NOTE_DISPLAY_MAX_LENGTH
            ? `${note.slice(0, NOTE_DISPLAY_MAX_LENGTH)}…`
            : note;
    }
}
